import React, {useState, useEffect} from 'react';
import {Outlet, useNavigate, useLocation, Link as RouterLink} from 'react-router-dom';
import {
    AppBar, Box, CssBaseline, Drawer, IconButton, List, Toolbar, Typography, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CategoryIcon from '@mui/icons-material/Category';
import ComputerIcon from '@mui/icons-material/Computer';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import PeopleIcon from '@mui/icons-material/People';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import HubIcon from "@mui/icons-material/Hub";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CasinoIcon from '@mui/icons-material/Casino';
import TokenIcon from '@mui/icons-material/Token';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import {Coins, ListOrdered} from "lucide-react";

import UserProfile from './components/UserProfile';
import DynamicBreadcrumbs from './components/DynamicBreadcrumbs';
import MenuItem from "./components/MenuItem";

const drawerWidth = 240;

const menuConfig = [
    {to: "/machines", icon: <ComputerIcon/>, text: "Machines"},
    {to: "/queues", icon: <ListOrdered size={24}/>, text: "Queues"},
    {to: "/players", icon: <PeopleIcon/>, text: "Players"},
    {to: "/transactions", icon: <ReceiptLongIcon/>, text: "Transactions"},
    {to: "/games", icon: <SportsEsportsIcon/>, text: "Games"},
    {to: "/game-sessions", icon: <HubIcon/>, text: "Game Sessions"},
    {to: "/rewards", icon: <CardGiftcardIcon/>, text: "Rewards"},
    {to: "/currencies", icon: <Coins size={24}/>, text: "Currencies"},
    {to: "/categories", icon: <CategoryIcon/>, text: "Categories"},
    {to: "/daily-bonus-configs", icon: <CalendarTodayIcon/>, text: "Daily Bonus"},
    {to: "/challenges", icon: <EmojiEventsIcon/>, text: "Challenges"},
    {to: "/levelplay-placements", icon: <OndemandVideoIcon/>, text: "LevelPlay"},
    {type: 'divider'},
    {
        icon: <CasinoIcon/>,
        text: "Minigames",
        subItems: [
            {to: "/minigames", icon: <SportsEsportsIcon/>, text: "Games"},
            {to: "/gelts", icon: <TokenIcon/>, text: "Gelts"},
            {to: "/xp-level-configs", icon: <TrendingUpIcon/>, text: "XP Levels"},
            {to: "/minigames/stats", icon: <AssessmentIcon/>, text: "Statistics"},
        ]
    },
    {
        icon: <ShoppingCartIcon/>,
        text: "Prize Swaps",
        subItems: [
            {to: "/prize-categories", icon: <CategoryIcon/>, text: "Categories"},
            {to: "/prize-items", icon: <CardGiftcardIcon/>, text: "Items"},
        ]
    }
];

const Layout = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    // State for the desktop drawer
    const [isDrawerOpen, setDrawerOpen] = useState(true);
    const [swapsOpen, setSwapsOpen] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
            sessionStorage.removeItem('redirectAfterLogin');
            navigate(redirectPath);
        }
    }, [navigate]);

    useEffect(() => {
        if (location.pathname.startsWith('/prize-')) {
            setSwapsOpen(true);
        }
    }, [location.pathname]);

    const handleMobileDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleDesktopDrawerToggle = () => {
        setDrawerOpen(!isDrawerOpen);
    };

    const drawerContent = (
        <div>
            {/* Brand banner — official logo on the brand-pink background so the
                white-stroke SVG reads correctly. Doubles as the drawer's
                Toolbar-height header so AppBar and drawer stay vertically
                aligned across breakpoints. */}
            <Box
                component={RouterLink}
                to="/"
                sx={{
                    minHeight: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    py: 1.5,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    textDecoration: 'none',
                    transition: 'background-color 150ms ease-out',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:focus-visible': {
                        outline: (theme) => `2px solid ${theme.palette.common.white}`,
                        outlineOffset: -2,
                    },
                }}
            >
                <Box
                    component="img"
                    src="/Nexal-logo.svg"
                    alt="Nexal Admin"
                    sx={{ height: 28, width: 'auto', display: 'block' }}
                />
            </Box>
            <Divider/>
            <List>
                {menuConfig.map((item, index) => (
                    item.type === 'divider'
                        ? <Divider key={`divider-${index}`} sx={{my: 1}}/>
                        : <MenuItem key={item.text} item={item}/>
                ))}
            </List>
        </div>
    );

    return (
        <Box sx={{display: 'flex'}}>
            <CssBaseline/>
            <AppBar
                position="fixed"
                sx={{
                    width: {sm: `calc(100% - ${isDrawerOpen ? drawerWidth : 0}px)`},
                    ml: {sm: `${isDrawerOpen ? drawerWidth : 0}px`},
                    transition: (theme) => theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                <Toolbar>
                    {/* Button for mobile */}
                    <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleMobileDrawerToggle}
                                sx={{mr: 2, display: {sm: 'none'}}}>
                        <MenuIcon/>
                    </IconButton>
                    {/* Button for desktop */}
                    <IconButton color="inherit" aria-label="toggle drawer" edge="start"
                                onClick={handleDesktopDrawerToggle} sx={{mr: 2, display: {xs: 'none', sm: 'block'}}}>
                        <MenuIcon/>
                    </IconButton>
                    {/* Brand on the AppBar shows only on mobile — desktop has
                        the persistent drawer with the same logo. */}
                    <Box
                        component={RouterLink}
                        to="/"
                        aria-label="Nexal Admin home"
                        sx={{
                            display: {xs: 'flex', sm: 'none'},
                            alignItems: 'center',
                            mr: 1,
                            textDecoration: 'none',
                            '&:focus-visible': {
                                outline: (theme) => `2px solid ${theme.palette.common.white}`,
                                outlineOffset: 2,
                                borderRadius: 1,
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src="/Nexal-logo.svg"
                            alt=""
                            sx={{ height: 22, width: 'auto', display: 'block' }}
                        />
                    </Box>
                    <Typography variant="h6" noWrap component="div" sx={{flexGrow: 1}}/>
                    <UserProfile/>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{width: {sm: drawerWidth}, flexShrink: {sm: 0}}}
                aria-label="mailbox folders"
            >
                {/* Mobile Drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleMobileDrawerToggle}
                    ModalProps={{keepMounted: true}}
                    sx={{
                        display: {xs: 'block', sm: 'none'},
                        '& .MuiDrawer-paper': {boxSizing: 'border-box', width: drawerWidth},
                    }}
                >
                    {drawerContent}
                </Drawer>
                {/* Desktop Drawer */}
                <Drawer
                    variant="persistent"
                    sx={{
                        display: {xs: 'none', sm: 'block'},
                        '& .MuiDrawer-paper': {boxSizing: 'border-box', width: drawerWidth},
                    }}
                    open={isDrawerOpen}
                >
                    {drawerContent}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    // Tighter padding on phones so tables/cards get the full
                    // viewport; full padding from sm up.
                    p: {xs: 1.5, sm: 3},
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                    // Stop the flex column expanding past the viewport when a
                    // wide table mounts — child can scroll horizontally instead.
                    minWidth: 0,
                    transition: (theme) => theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    ml: {sm: `-${drawerWidth}px`},
                    ...(isDrawerOpen && {
                        transition: (theme) => theme.transitions.create('margin', {
                            easing: theme.transitions.easing.easeOut,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        marginLeft: {sm: 0},
                    }),
                }}
            >
                <Toolbar/>
                <Box component="div" sx={{flexGrow: 1}}>
                    <DynamicBreadcrumbs/>
                    <Outlet/>
                </Box>
                <Box component="footer" sx={{
                    textAlign: 'center',
                    mt: 'auto',
                    py: 2,
                    color: 'text.secondary',
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }}>
                    <Typography variant="body2">© {new Date().getFullYear()} Nexalgames Inc.</Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default Layout;